import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { SearchParamsPagination } from 'src/components/Pagination';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { externalLinks } from 'src/externalLinks';
import { UserAssessment } from 'src/models/assessment/_types';
import {
  deleteUserAssessment,
  exportUserAssessment,
  fetchUserAssessments,
} from 'src/models/assessment/assessment';
import { getUserDisplayName } from 'src/models/user/utils';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { formatMonthDayYear } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { AttachToAccountDialog } from './AttachToAccountDialog';

export const LeadsPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessments',
  });
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { params, updateParam } = useListSearchParams({
    defaultPerPage: 20,
  });
  const [confirmDeleteProps, confirm] = useConfirmDialogComponent();
  const [selectedLeads, setSelectedLeads] = useState<UserAssessment[]>([]);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachAssessmentId, setAttachAssessmentId] =
    useState<Identifier | null>(null);

  // Fetch lead assessments
  const { data, isLoading } = useQuery({
    queryKey: ['leadAssessments', params.page, params.search, params.perPage],
    queryFn: () =>
      fetchUserAssessments({
        page: params?.page || 1,
        perPage: params?.perPage,
        searchTerm: params?.search,
        lead: true,
        forTrainer: true,
        withAssessments: true,
        withUsers: true,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteUserAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['leadAssessments'],
      });
      toast.success(tAssessment('deleteSuccessMessage'));
    },
    onError: (error: any) => {
      toast.error(api.formatError(error));
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: exportUserAssessment,
    onSuccess: (resourceId) => {
      const downloadUrl = `/resources/${resourceId}/download/`;
      window.open(externalLinks.v2Url(downloadUrl), '_blank');
    },
    onError: () => {
      toast.error(tAssessment('exportFailedMessage'));
    },
  });

  // Handle delete
  const handleDelete = async (assessment: UserAssessment) => {
    if (
      await confirm({
        title: tAssessment('deleteAssessment'),
        message: t('deleteLeadConfirm'),
      })
    ) {
      deleteMutation.mutate(assessment.id);
    }
  };

  // Handle export/download
  const handleDownload = (assessmentId: Identifier) => {
    toast.success(tCommon('exportingInProgress'));
    exportMutation.mutate(assessmentId);
  };

  // Handle invite as client
  const handleInviteAsClient = (assessment: UserAssessment) => {
    const name = assessment.leadName?.split(' ') || [];
    const firstName = name[0] || '';
    const lastName = name.slice(1).join(' ') || '';
    const email = assessment.leadEmail || '';

    const params = new URLSearchParams({
      email,
      first_name: firstName,
      last_name: lastName,
    });

    navigate(`/clients/new?${params.toString()}`);
  };

  const handleInviteAsClientBatch = () => {
    const params = new URLSearchParams({
      emails: selectedLeads
        .filter((lead) => !lead.clientId)
        .map((lead) => lead.leadEmail)
        .join(','),
    });

    navigate(`/clients/new?${params.toString()}`);
  };

  // Handle attach to existing account
  const handleAttachToExistingAccount = (assessmentId: Identifier) => {
    setAttachAssessmentId(assessmentId);
    setAttachDialogOpen(true);
  };

  // Table columns
  const columns = [
    {
      id: 'info',
      label: t('info'),
      format: (_, row: UserAssessment) => {
        const displayName = row.userId
          ? getUserDisplayName(row.user) || 'User'
          : row.leadName || 'Lead';
        const displayEmail = row.userId ? row.user?.email : row.leadEmail || '';
        const isConverted = !!row.userId;

        return (
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {displayName}
              </Typography>
              {isConverted && (
                <Tooltip title={t('converted')}>
                  <Icon
                    icon={icon({ name: 'check', style: 'solid' })}
                    sx={{ color: 'success.main', fontSize: '12px' }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {displayEmail}
            </Typography>
          </Stack>
        );
      },
    },
    {
      id: 'assessmentName',
      label: tAssessment('one'),
    },
    {
      id: 'createdAt',
      label: t('submittedDate'),
      format: (value: number) => formatMonthDayYear(value, 'MM-dd-yyyy'),
    },
  ];

  // Table actions
  const actions = [
    {
      icon: icon({ name: 'eye', style: 'regular' }),
      label: t('results'),
      onClick: (assessment: UserAssessment) =>
        navigate(`/me/assessment/completed/${assessment.id}`),
    },
    {
      icon: icon({ name: 'user-cog', style: 'regular' }),
      label: t('manageClient'),
      onClick: (assessment: UserAssessment) =>
        navigate(`/clients/${assessment.clientId}/assessments`),
      showAction: (row: UserAssessment) => !!row.clientId,
    },
    {
      icon: icon({ name: 'user-plus', style: 'regular' }),
      label: t('addAsClient'),
      onClick: handleInviteAsClient,
      showAction: (row: UserAssessment) => !row.clientId,
    },
    {
      icon: icon({ name: 'download', style: 'regular' }),
      label: tAssessment('downloadAsCsv'),
      onClick: (assessment: UserAssessment) => handleDownload(assessment.id),
    },
    {
      icon: icon({ name: 'link', style: 'regular' }),
      label: t('attachToExistingAccount'),
      onClick: (assessment: UserAssessment) => handleAttachToExistingAccount(assessment.id),
      showAction: (row: UserAssessment) => !row.userId,
    },
    {
      icon: icon({ name: 'trash', style: 'regular' }),
      label: tAssessment('deleteAssessment'),
      onClick: handleDelete,
    },
  ];

  const batchActions = [
    {
      icon: icon({ name: 'user-plus', style: 'regular' }),
      label: t('addAsClients'),
      onClick: () => handleInviteAsClientBatch(),
    },
  ];

  return (
    <>
      <StickyCard title={t('completedLeads')} contentSx={{ p: 0 }}>
        <SectionToolbar>
          <SearchParamSearchTextField
            params={params}
            updateParam={updateParam}
            sx={{ width: '100%' }}
            placeholder={t('searchLeadsPlaceholder')}
          />
        </SectionToolbar>
        <DataTable
          columns={columns}
          rows={data?.userAssessments || []}
          selectableRows={true}
          noResultsText={t('noLeadsCompleted')}
          loading={isLoading}
          rowActions={actions}
          onClickRow={(row) => navigate(`/me/assessment/completed/${row.id}`)}
          selectedRows={selectedLeads}
          setSelectedRows={setSelectedLeads}
          batchActions={batchActions}
          fixedActions
        />

        {/* Pagination */}
        <SearchParamsPagination
          params={params}
          updateParam={updateParam}
          total={data?.total || 0}
          itemsPerPage={params.perPage}
        />
      </StickyCard>

      {/* Confirm dialog */}
      <ConfirmDialog {...confirmDeleteProps} />

      {/* Attach to account dialog */}
      <AttachToAccountDialog
        open={attachDialogOpen}
        onClose={() => setAttachDialogOpen(false)}
        userAssessmentId={attachAssessmentId}
      />
    </>
  );
};
